"use client";

import Image from "next/image";
import { Link } from "@/navigation";
import { useTranslations } from "next-intl";

const FINISH_ITEMS = [
	{ image: "as-machined.webp", titleKey: "asMachinedTitle", descKey: "asMachinedDesc" },
	{ image: "brushed-finish.webp", titleKey: "brushedFinishTitle", descKey: "brushedFinishDesc" },
	{ image: "polishing.webp", titleKey: "polishingTitle", descKey: "polishingDesc" },
	{ image: "sand-blasting.webp", titleKey: "sandBlastingTitle", descKey: "sandBlastingDesc" },
	{ image: "tumbling.webp", titleKey: "tumblingTitle", descKey: "tumblingDesc" },
	{ image: "anodizing.webp", titleKey: "anodizingTitle", descKey: "anodizingDesc" },
	{ image: "powder-coating.webp", titleKey: "powderCoatingTitle", descKey: "powderCoatingDesc" },
	{ image: "electroplating.webp", titleKey: "electroplatingTitle", descKey: "electroplatingDesc" },
	{ image: "black-oxide.webp", titleKey: "blackOxideTitle", descKey: "blackOxideDesc" },
	{ image: "alodine-coating.webp", titleKey: "alodineCoatingTitle", descKey: "alodineCoatingDesc" },
	{ image: "heat-treatment.webp", titleKey: "heatTreatmentTitle", descKey: "heatTreatmentDesc" },
	{ image: "passivation.webp", titleKey: "passivationTitle", descKey: "passivationDesc" },
] as const;

const DEFAULT_IMAGE_BASE = "/image/new-version/fabrication/metal-bending/surface-finishes";

export interface MetalBendingSurfaceFinishesProps {
	translationNamespace?: string;
	imageBasePath?: string;
	/** 底部「更多」按钮链接 */
	moreButtonHref?: string;
}

/**
 * 标题与副标题上下居中排列；网格与 AxisBmcl 一致。
 */
export default function MetalBendingSurfaceFinishes({
	translationNamespace = "metalBendingSurfaceFinishes",
	imageBasePath = DEFAULT_IMAGE_BASE,
	moreButtonHref = "/surface-finishing",
}: MetalBendingSurfaceFinishesProps) {
	const t = useTranslations(translationNamespace);

	return (
		<section className="w-full bg-[#ededed] py-8 sm:py-10 md:py-12 lg:py-16">
			<div className="container">
				<div className="mb-6 text-center sm:mb-8 md:mb-10 lg:mb-12">
					<h2 className="mb-3 text-2xl font-bold leading-tight text-black sm:mb-4 sm:text-[30px] md:text-4xl">
						{t("title")}
					</h2>
					<p className="text-sm leading-relaxed text-black md:text-base">{t("intro")}</p>
				</div>

				<div className="grid grid-cols-2 gap-0 md:grid-cols-3 lg:grid-cols-4">
					{FINISH_ITEMS.map((item) => (
						<div key={item.image} className="flex flex-col border border-[#bfbfbf] p-4 md:p-10">
							<h3 className="mb-3 text-sm font-bold text-black md:text-base">{t(item.titleKey)}</h3>
							<div className="relative mb-4 aspect-square w-full overflow-hidden">
								<Image
									src={`${imageBasePath}/${item.image}`}
									alt={t(item.titleKey)}
									fill
									className="object-contain"
									sizes="(max-width: 640px) 50vw, (max-width: 1024px) 33vw, 25vw"
								/>
							</div>
							<p className="text-sm !leading-relaxed text-black">{t(item.descKey)}</p>
						</div>
					))}
				</div>

				<div className="mt-12 flex justify-center md:mt-16">
					<Link
						href={moreButtonHref}
						className="inline-flex items-center justify-center rounded bg-themePrimary400 px-6 py-3 text-sm font-medium text-white transition-all duration-300 hover:bg-[#5a9420] hover:text-white md:px-8 md:py-3.5 md:text-base"
					>
						{t("moreButton")}
					</Link>
				</div>
			</div>
		</section>
	);
}
